import React from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { AdminProductOption } from "../../../services/platformApi";
import ManualCertificateConfirmationModal from "./ManualCertificateConfirmationModal";
import SectionLoadingOverlay from "./SectionLoadingOverlay";

interface ManualCertificateSectionProps {
  certificateEnabledProducts: AdminProductOption[];
  manualSelectedProduct: string;
  manualName: string;
  manualSurname: string;
  manualBirthDate: string;
  manualEmail: string;
  manualOptionalEmail: string;
  manualCompany: string;
  manualFieldCode: string;
  manualProductCode: string;
  manualHours: string;
  manualDate: string;
  manualCountNumber: string;
  manualPdfBlob: Blob | null;
  manualActionLoading: boolean;
  manualMessage: string;
  manualError: string;
  manualLastSavedCountNumber: string;
  manualCertificationCheckLoading: boolean;
  manualCertificationStatus: "idle" | "certified" | "not-certified";
  manualCertificationMessage: string;
  manualSelectedProductTitle: string;
  isManualCertificationCheckCurrent: boolean;
  isManualConfirmationModalOpen: boolean;
  requiresManualFieldCode: boolean;
  fieldCodeOptions: readonly string[];
  productsLoading: boolean;
  onManualProductChange: (value: string) => void;
  onManualNameChange: (value: string) => void;
  onManualSurnameChange: (value: string) => void;
  onManualBirthDateChange: (value: string) => void;
  onManualEmailChange: (value: string) => void;
  onManualOptionalEmailChange: (value: string) => void;
  onManualCompanyChange: (value: string) => void;
  onManualFieldCodeChange: (value: string) => void;
  onManualDateChange: (value: string) => void;
  onManualCertificationCheck: () => void;
  onManualCompletion: () => void;
  onCloseManualConfirmationModal: () => void;
  onConfirmManualCompletion: () => void;
  onDownloadManualPreview: () => void;
}

const ManualCertificateSection: React.FC<ManualCertificateSectionProps> = ({
  certificateEnabledProducts,
  manualSelectedProduct,
  manualName,
  manualSurname,
  manualBirthDate,
  manualEmail,
  manualOptionalEmail,
  manualCompany,
  manualFieldCode,
  manualProductCode,
  manualHours,
  manualDate,
  manualCountNumber,
  manualPdfBlob,
  manualActionLoading,
  manualMessage,
  manualError,
  manualLastSavedCountNumber,
  manualCertificationCheckLoading,
  manualCertificationStatus,
  manualCertificationMessage,
  manualSelectedProductTitle,
  isManualCertificationCheckCurrent,
  isManualConfirmationModalOpen,
  requiresManualFieldCode,
  fieldCodeOptions,
  productsLoading,
  onManualProductChange,
  onManualNameChange,
  onManualSurnameChange,
  onManualBirthDateChange,
  onManualEmailChange,
  onManualOptionalEmailChange,
  onManualCompanyChange,
  onManualFieldCodeChange,
  onManualDateChange,
  onManualCertificationCheck,
  onManualCompletion,
  onCloseManualConfirmationModal,
  onConfirmManualCompletion,
  onDownloadManualPreview,
}) => {
  const isSectionLoading = productsLoading || manualCertificationCheckLoading || manualActionLoading;

  return (
    <section className="admin-page__card">
      {isSectionLoading && (
        <SectionLoadingOverlay
          label={
            manualActionLoading
              ? "Registruojamas pažymėjimas..."
              : manualCertificationCheckLoading
                ? "Tikrinama sertifikavimo būsena..."
                : "Kraunami galimi produktai..."
          }
        />
      )}

      <div className="admin-page__card-header">
        <h3>3. Rankinis pažymėjimo užbaigimas su registracija</h3>
        <p>
          Ši forma ne tik sugeneruoja pažymėjimą, bet ir išsaugo įrašą produkto
          kolekcijoje bei saugiai padidina pažymėjimo numerį.
        </p>
      </div>

      <div className="admin-page__manual-certificate-grid">
        <label className="admin-page__field">
          <span>*Produktas</span>
          <select value={manualSelectedProduct} onChange={(event) => onManualProductChange(event.target.value)}>
            <option value="">Pasirinkite produktą</option>
            {certificateEnabledProducts.map((product) => (
              <option key={product.key} value={product.key}>
                {product.title}
              </option>
            ))}
          </select>
        </label>

        <label className="admin-page__field">
          <span>*El. paštas</span>
          <input
            type="email"
            value={manualEmail}
            onChange={(event) => onManualEmailChange(event.target.value)}
            placeholder="pvz. vardas@example.com"
          />
        </label>

        <label className="admin-page__field">
          <span>Papildomas gavėjas</span>
          <input
            type="email"
            value={manualOptionalEmail}
            onChange={(event) => onManualOptionalEmailChange(event.target.value)}
            placeholder="Neprivaloma papildoma kopija"
          />
        </label>

        <label className="admin-page__field">
          <span>*Vardas</span>
          <input value={manualName} onChange={(event) => onManualNameChange(event.target.value)} />
        </label>

        <label className="admin-page__field">
          <span>*Pavardė</span>
          <input
            value={manualSurname}
            onChange={(event) => onManualSurnameChange(event.target.value)}
          />
        </label>

        <label className="admin-page__field">
          <span>*Gimimo data</span>
          <input
            type="date"
            value={manualBirthDate}
            onChange={(event) => onManualBirthDateChange(event.target.value)}
          />
        </label>

        <label className="admin-page__field">
          <span>Įmonė</span>
          <input
            value={manualCompany}
            onChange={(event) => onManualCompanyChange(event.target.value)}
            placeholder="Neprivaloma"
          />
        </label>

        <label className="admin-page__field">
          <span>*Data</span>
          <input value={manualDate} onChange={(event) => onManualDateChange(event.target.value)} />
        </label>

        <label className="admin-page__field">
          <span>Rezervuojamas numeris</span>
          <input value={manualCountNumber} readOnly />
        </label>

        {requiresManualFieldCode ? (
          <label className="admin-page__field">
            <span>*Veiklos srities kodas</span>
            <select value={manualFieldCode} onChange={(event) => onManualFieldCodeChange(event.target.value)}>
              <option value="">Pasirinkite kodą</option>
              {fieldCodeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="admin-page__field">
            <span>*Veiklos srities kodas</span>
            <input value={manualFieldCode} readOnly />
          </label>
        )}

        <label className="admin-page__field">
          <span>*Produkto kodas</span>
          <input value={manualProductCode} readOnly />
        </label>

        <label className="admin-page__field">
          <span>*Valandos</span>
          <input value={manualHours} readOnly />
        </label>
      </div>

      {manualLastSavedCountNumber && (
        <div className="admin-page__summary">
          <strong>Paskutinis sėkmingas registruotas numeris</strong>
          <div className="admin-page__summary-grid">
            <span>{manualLastSavedCountNumber}</span>
          </div>
        </div>
      )}

      {manualError && <p className="admin-page__feedback admin-page__feedback--error">{manualError}</p>}
      {manualMessage && (
        <p className="admin-page__feedback admin-page__feedback--success">{manualMessage}</p>
      )}

      <div className="admin-page__actions">
        <button onClick={onManualCertificationCheck} disabled={isSectionLoading}>
          {manualCertificationCheckLoading
            ? "Tikrinama..."
            : "Patikrinti ar naudotojas jau sertifikuotas"}
        </button>
        <button
          className="admin-page__button admin-page__button--primary"
          onClick={onManualCompletion}
          disabled={isSectionLoading || !isManualCertificationCheckCurrent}
        >
          {manualActionLoading ? "Registruojama..." : "Užregistruoti pažymėjimą ir atsisiųsti PDF"}
        </button>
        <button onClick={onDownloadManualPreview} disabled={isSectionLoading || !manualPdfBlob}>
          Atsisiųsti peržiūros PDF
        </button>
      </div>

      <div className="admin-page__summary">
        <strong>Sertifikavimo patikra</strong>
        {manualCertificationStatus === "idle" ? (
          <p className="admin-page__selected-products-empty">
            Pirmiausia patikrinkite pasirinktą el. paštą pagal pasirinktą produktą.
          </p>
        ) : (
          <div className="admin-page__check-result">
            <span
              className={`admin-page__status-pill ${
                manualCertificationStatus === "certified"
                  ? "admin-page__status-pill--success"
                  : "admin-page__status-pill--muted"
              }`}
            >
              {manualCertificationStatus === "certified"
                ? "Jau sertifikuotas"
                : "Dar nesertifikuotas"}
            </span>
            <p>{manualCertificationMessage}</p>
          </div>
        )}
      </div>

      {manualPdfBlob && (
        <div className="admin-page__pdf-preview">
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer fileUrl={URL.createObjectURL(manualPdfBlob)} />
          </Worker>
        </div>
      )}

      <ManualCertificateConfirmationModal
        isOpen={isManualConfirmationModalOpen}
        email={manualEmail.trim()}
        productTitle={manualSelectedProductTitle}
        loading={manualActionLoading}
        onClose={onCloseManualConfirmationModal}
        onConfirm={onConfirmManualCompletion}
      />
    </section>
  );
};

export default ManualCertificateSection;
